import React, { useState } from "react"
import { get } from "lodash"
import {
  Box,
  Stack,
  Button,
  IconButton,
  Dialog,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tooltip,
} from "@mui/material"
import { css, Global } from "@emotion/react"
import { makeStyles } from "@material-ui/core"
import CloseIcon from "@mui/icons-material/Close"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"

import { showSuccess, showError } from "../v2_actions/error"
import { PhotographUploader } from "./form/PhotographUploader"
import { useDispatch } from "react-redux"
import { fileInfoList } from "v2_actions/file_info"
import LoadingSpinner from "../components/LoadingSpinner"

const useStyles = makeStyles((theme) => ({
  alertDialogHeading: {
    color: "#000",
    fontWeight: 700,
    textAlign: "center",
    [theme.breakpoints.down("md")]: {},
  },
  dialogTitle: {
    fontSize: "20px",
    fontWeight: 800,
    color: "#333",
  },
  logoOptionsSection: {
    marginTop: "20px",
    padding: "15px 20px",
    borderTop: "1px solid #e0e0e0",
    display: "flex",
    justifyContent: "center",
    gap: "80px",
  },
  logoOptionBox: {
  },
  logoOptionLabel: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#333",
    marginBottom: "8px",
  },
  radioLabel: {
    fontSize: "12px",
    color: "#555",
  },
  orText: {
    fontSize: "12px",
    fontWeight: 500,
    color: "#666",
  },
}))

const global_styles = css`
  div.MuiDialog-paperScrollPaper {
    width: 500px;
    border-radius: 12px;
    border: solid #ddd 1px;
  }
  .MuiFormControlLabel-label {
    font-size: 12px !important;
    color: #555 !important;
  }
  .MuiFormLabel-root {
    font-size: 14px !important;
    font-weight: 600 !important;
    color: #333 !important;
  }
`

export const AddGalleryEventLogoDialog = ({ handleClose, setCustomGalleryEventLogo }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const [
    isUploadingCustomGalleryEventLogo,
    setIsUploadingCustomGalleryEventLogo,
  ] = useState(false)
  const [logoSize, setLogoSize] = useState('small')
  const [logoPosition, setLogoPosition] = useState('bottom_left')
  const [logoSource, setLogoSource] = useState(null) // 'custom' or 'event'
  const [uploadedFileInfo, setUploadedFileInfo] = useState(null)

  const logoSizeToPercentage = {
    small: 13,
    medium: 23,
    large: 33,
  }

  const handleLogoSizeChange = (event) => {
    setLogoSize(event.target.value)
  }

  const handleLogoPositionChange = (event) => {
    setLogoPosition(event.target.value)
  }

  const handleUploadCustomLogo = (files) => {
    setIsUploadingCustomGalleryEventLogo(true)
    dispatch(fileInfoList.upload(files[0]))
      .then((file_info) => {
        setUploadedFileInfo(file_info)
        setLogoSource('custom')
      })
      .catch((error) => {
        showError("Error", "File upload failed")
        console.error(error)
      })
      .finally(() => setIsUploadingCustomGalleryEventLogo(false))
  }

  const handleUseEventLogo = () => {
    setLogoSource('event')
    setUploadedFileInfo(null)
  }

  const handleSubmit = () => {
    if (logoSource === 'custom' && uploadedFileInfo) {
      setCustomGalleryEventLogo(
        "custom_gallery_event_logo",
        get(uploadedFileInfo, "id"),
        { shouldValidate: true }
      )
    } else if (logoSource === 'event') {
      setCustomGalleryEventLogo("custom_gallery_event_logo", null, {
        shouldValidate: true,
      })
    }
    // Set logo size and position
    setCustomGalleryEventLogo(
      "logo_size",
      logoSizeToPercentage[logoSize],
      { shouldValidate: true }
    )
    setCustomGalleryEventLogo(
      "logo_position",
      logoPosition,
      { shouldValidate: true }
    )
    handleClose()
  }

  const renderPhotoUploader = () => {
    return (
      <Stack
        direction="column"
        alignItems="center"
        justifyContent="space-evenly"
      >
        <Box>
          <PhotographUploader
            isEditorForLogoUpload={true}
            uploadMessage='Drag and Drop or Click to add Logo'
            onChange={({ files }) => handleUploadCustomLogo(files)}
          />
        </Box>
      </Stack>
    )
  }

  const renderLogoSourceStatus = () => {
    if (logoSource === 'custom' && uploadedFileInfo) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'green', mt: 1 }}>
          <CheckCircleIcon fontSize="small" />
          <Typography variant="body2">Custom logo uploaded</Typography>
        </Box>
      )
    } else if (logoSource === 'event') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'green', mt: 1 }}>
          <CheckCircleIcon fontSize="small" />
          <Typography variant="body2">Using event logo</Typography>
        </Box>
      )
    }
    return null
  }

  const renderLogoOptions = () => {
    return (
      <Box className={classes.logoOptionsSection}>
        <Box className={classes.logoOptionBox}>
          <FormControl component="fieldset">
            <FormLabel component="legend" className={classes.logoOptionLabel}>
              Logo Size
            </FormLabel>
            <RadioGroup
              value={logoSize}
              onChange={handleLogoSizeChange}
            >
              <Tooltip title="13% of photo height" placement="right" arrow>
                <FormControlLabel value="small" control={<Radio />} label="Small" />
              </Tooltip>
              <Tooltip title="23% of photo height" placement="right" arrow>
                <FormControlLabel value="medium" control={<Radio />} label="Medium" />
              </Tooltip>
              <Tooltip title="33% of photo height" placement="right" arrow>
                <FormControlLabel value="large" control={<Radio />} label="Large" />
              </Tooltip>
            </RadioGroup>
          </FormControl>
        </Box>
        <Box className={classes.logoOptionBox}>
          <FormControl component="fieldset">
            <FormLabel component="legend" className={classes.logoOptionLabel}>
              Logo Position
            </FormLabel>
            <RadioGroup
              value={logoPosition}
              onChange={handleLogoPositionChange}
            >
              <FormControlLabel value="bottom_left" control={<Radio />} label="Bottom Left" />
              <FormControlLabel value="bottom_right" control={<Radio />} label="Bottom Right" />
              <FormControlLabel value="top_left" control={<Radio />} label="Top Left" />
              <FormControlLabel value="top_right" control={<Radio />} label="Top Right" />
            </RadioGroup>
          </FormControl>
        </Box>
      </Box>
    )
  }

  return (
    <div>
      <Global styles={global_styles} />
      <Dialog
        open
        mb={2}
        keepMounted
        disablePortal={true}
        onClose={handleClose}
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle className={classes.alertDialogHeading}>
          <Typography
            sx={{
              mt: 2,
              fontSize: "20px",
              fontWeight: 800,
              color: "#333"
            }}
          >
            Upload Logos
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              top: 8,
              right: 8,
              position: "absolute",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        {isUploadingCustomGalleryEventLogo && <LoadingSpinner />}
        <DialogContent>
          <Stack
            spacing={2}
            direction="row"
            alignItems="center"
            justifyContent="space-evenly"
          >
            <Box>{renderPhotoUploader()}</Box>
            <Box className={classes.orText}>Or</Box>
            <Box>
              <Button
                variant={logoSource === 'event' ? "contained" : "outlined"}
                onClick={handleUseEventLogo}
              >
                <b>Use Event Logo</b>
              </Button>
            </Box>
          </Stack>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            {renderLogoSourceStatus()}
          </Box>
          {renderLogoOptions()}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!logoSource}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}
